/* Copyright (c) 2020-2024 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useState, useEffect, ReactNode } from "react";
import { Container } from "react-awesome-styled-grid";
import { Switch, Case, Default } from "react-if";
import {
  Heading,
  Spacer,
  theme,
  FlexContainer,
  SVGIcon,
  Text,
} from "@nordcloud/gnui";
import { useCustomerQuery } from "~/generated/graphql";
import { ErrorMessage } from "~/components/Error";
import { LoaderWrap } from "~/components/Loader";
import { CustomerCtxProvider } from "~/context/CustomerSelector/customerProvider";
import { useQueryState } from "~/hooks";
import { Customer } from "~/types";
import { CustomerSelectView } from "./CustomerSelectView";
import {
  removeCustomerFromLS,
  saveCustomerInLS,
  getCustomerFromLS,
} from "./utils";

type Props = {
  children: ReactNode;
};

const emptyCustomer = { id: "", name: "", organizationName: "" };

export function CustomerSelector({ children }: Props) {
  const [customer, setCustomer] = useState<Customer>(emptyCustomer);
  const {
    queryState: { customerId },
    removeQueryState,
  } = useQueryState<{ customerId?: string }>();
  const { data, loading, error } = useCustomerQuery();

  const selectCustomer = useCallback((c: Customer) => {
    saveCustomerInLS(c);
    setCustomer(c);
  }, []);

  const chooseCustomer = async (c: Customer) => {
    selectCustomer(c);
  };

  const resetCustomer = useCallback(() => {
    removeCustomerFromLS();
    setCustomer(emptyCustomer);
  }, []);

  const findCustomer = data?.customers?.find((c) => c.id === customerId);

  const hasMoreCustomers =
    (data?.customers && data?.customers?.length > 1) || false;

  useEffect(() => {
    if (findCustomer) {
      selectCustomer(findCustomer);
      removeQueryState(["customerId"]);
    }
  }, [findCustomer, selectCustomer, removeQueryState]);

  useEffect(() => {
    if (!findCustomer && customerId) {
      resetCustomer();
    }
  }, [customerId, findCustomer, resetCustomer]);

  useEffect(() => {
    const { id, name, organizationName } = getCustomerFromLS();

    if (id && name && organizationName) {
      setCustomer({
        id,
        name,
        organizationName,
      });
    }
  }, [setCustomer]);

  const setCustomerValue = (c: Customer) => setCustomer(c);

  const customers: Customer[] = data?.customers.map((c) => {
    const { id, name, organizationName } = c;
    return {
      id,
      name,
      organizationName,
    };
  }) ?? [emptyCustomer];

  const submitCustomer = (id: string, name: string, orgName: string) => {
    chooseCustomer({ id, name, organizationName: orgName });
  };

  return (
    <CustomerCtxProvider
      value={{ customer, hasMoreCustomers, setCustomerValue }}
    >
      <LoaderWrap loading={loading}>
        <Switch>
          <Case condition={data?.customers.length === 0}>
            <Container>
              <FlexContainer justifyContent="center" css={{ height: "80vh" }}>
                <FlexContainer direction="column">
                  <Spacer height={theme.spacing.spacing07} />
                  <SVGIcon name="bulb" size="xxl" />
                  <Spacer height={theme.spacing.spacing07} />
                  <Heading level={2}>
                    You don&apos;t have access to any customers.
                  </Heading>
                  <Text align="center">
                    You can request access from{" "}
                    <Mailto email="products@nordcloudapp.com" />.
                  </Text>
                </FlexContainer>
              </FlexContainer>
            </Container>
          </Case>
          <Case condition={!customer.id && !findCustomer}>
            <Container>
              <Spacer height={theme.spacing.spacing07} />
              <ErrorMessage error={error} />

              <CustomerSelectView
                customers={customers}
                onSubmit={submitCustomer}
              />
            </Container>
          </Case>

          <Default>{children}</Default>
        </Switch>
      </LoaderWrap>
    </CustomerCtxProvider>
  );
}

function Mailto({ email }: { email: string }) {
  return <a href={`mailto:${email}`}>{email}</a>;
}
